import {
  trackApplyStandardsApplied,
  trackUpdateStandardsApplied,
} from '@src/analytics/EventTracker';
import { PARAGRAPH_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import {
  NoShowcaseMessage,
  SaveButton as SaveButtonKaraokeBase,
} from '@src/components/sidepanel/KaraokeSettings/KaraokeSaveOrCancelTemplate';
import { createCompositionElement } from '@src/services/composition/CompositionElementService';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import {
  applyControlOverrides,
  applyLabelOverridesToSource,
  applyTemplateStandardsToElement,
  buildApplyStandardsRows,
  modifyCompositionProperty,
  refillSpeakerForCompositionElement,
} from '@src/utility/compositionSettings';
import {
  deleteElementMeta,
  getElementMetaObject,
  storeCompositionTemplateMetadata,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import {
  addCompanyLine,
  carriedOverEdits,
  minFramingScale,
  removeCompanyLine,
} from '@src/utility/nametagCompanyLine';
import { applySourcePropertyOverrides } from '@src/utility/standardsSourceProperties';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { useCompositionContext } from '../CompositionContext';
import { SaveNameModal } from '../SaveNameModal';
import { ApplyStandardsModal } from './ApplyStandardsModal';
import { UpdateStandardsModal } from './UpdateStandardsModal';

import type { CompositionActions } from '../CompositionContext';
import type {
  AspectConfigKey,
  AspectConfiguration,
  CompositionTemplate,
  ControlConfiguration,
} from './types';
import type { PlacementParts } from './UpdateStandardsModal';
import type { ElementState } from '@src/renderer/ElementState';
import type {
  ControlOverride,
  EditorStoreLike,
} from '@src/utility/compositionSettings';
import type {
  ElementLayoutReadout,
  ElementPlacementParts,
} from '@src/utility/quoteCardLayout';
import type { SourcePropertyOverride } from '@src/utility/standardsSourceProperties';

const UPDATE_CLICK_DELAY = 750;

export const CompositionSaveTemplate = ({
  composition,
  updatedValues,
  categoryId,
  subcategoryId,
  activeElement,
  editorStore,
  saveMeta = true,
  onAfterCreate,
  onAfterSave,
  positionDirty,
  getCurrentAspectPlacement,
  getSourceForSave,
  elementLayout,
  standardSource,
}: {
  composition: CompositionTemplate;
  updatedValues: {
    elementPaths: number[][];
    value: unknown;
    property: string;
  }[];
  categoryId: string;
  subcategoryId: string;
  activeElement: ElementState;
  editorStore?: EditorStoreLike;
  saveMeta?: boolean;
  onAfterCreate?: (response: CompositionTemplate) => void;
  onAfterSave?: () => void;
  positionDirty?: boolean;
  getCurrentAspectPlacement?: () =>
    | { key: AspectConfigKey; config: AspectConfiguration }
    | undefined;
  /**
   * The source to write for this save. Pull Quotes merge only the moved
   * element geometry into the template's own source (PLAT-11977); without it
   * `composition.source` is written as-is (Creator Studio).
   */
  getSourceForSave?: (
    elementParts: ElementPlacementParts,
    action: CompositionActions,
  ) => CompositionTemplate['source'];
  /** Every element's position and size, template vs card (Pull Quotes). */
  elementLayout?: ElementLayoutReadout[];
  /** The template's own source when `composition.source` is the card on
   *  screen (Pull Quotes), for the modal's More fields. */
  standardSource?: CompositionTemplate['source'];
}) => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { enableApplyStandardsPreview } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const [updating, setUpdating] = useState<Date | null>(null);
  const [modalAction, setModalAction] = useState<CompositionActions | null>(
    null,
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  const meta = getElementMetaObject(
    videoCreator.currentVideo,
    activeElement.source.id,
  );

  const { addUpdateFavoriteCompositionTemplate } = useCompositionContext();

  // Re-creates the nametag on screen from a saved template, at the same time,
  // for the same length and on the same track, keeping its speaker and every
  // edit the save left out, then removes the old copy (PLAT-12130).
  const replaceElementFromTemplate = async (
    template: CompositionTemplate,
    store: EditorStoreLike,
  ) => {
    const oldId = activeElement.source.id;
    const oldMeta = getElementMetaObject(videoCreator.currentVideo, oldId);
    const time = Number(activeElement.source.time);
    const duration = Number(activeElement.source.duration);
    const trackId = store.editor.timeline.getTrackIdForElement(oldId);
    const carried = carriedOverEdits(
      store.editor.timeline.getElement(oldId)?.toJSON(),
      template.controls,
      composition.controls,
    );
    const result = await createCompositionElement({
      // The old copy's range, so a trimmed nametag keeps its length; a
      // template whose placement pins an edge still derives its range.
      ...(Number.isFinite(time) && Number.isFinite(duration) && duration > 0
        ? { timeRange: { time, duration } }
        : {}),
      ...(trackId ? { targetTrackId: trackId } : {}),
      template,
      videoProps: {
        duration: videoCreator.duration,
        getCurrentVideo: () => videoCreator.currentVideo,
        aspectRatio: videoCreator.getCurrentAspectRatio(),
        time: Number.isFinite(time) ? time : videoCreator.time,
        organization: videoCreator.organization,
        story: videoCreator.story,
        convertToVh: (v: string) => videoCreator.textProducer.convertToVh(v),
      },
      editorStore: store,
      upsertExtraElementDataCb: (elementId, saved) => {
        storeCompositionTemplateMetadata(
          videoCreator.currentVideo,
          elementId,
          saved,
        );
      },
    });
    if (!result.ok || !result.value.elementId) {
      videoCreator.showToast({
        state: 'error',
        message:
          'Saved. Re-add this nametag to see its new lines; it could not be refreshed here.',
        delay: 5000,
      });
      return;
    }
    const newId = result.value.elementId;
    // No undo entries: the create above is the one undo step of this rebuild.
    if (Object.keys(carried.placement).length > 0)
      store.editor.commandUpdateElement(newId, carried.placement, false);
    for (const { elementPaths, property, value } of carried.values)
      modifyCompositionProperty(
        newId,
        store,
        elementPaths,
        property,
        value,
        false,
      );
    if (typeof oldMeta?.speakerLabel === 'string')
      upsertElementMetaObject(videoCreator.currentVideo, newId, {
        speakerLabel: oldMeta.speakerLabel,
      });
    store.editor.commandRemoveElement(oldId, false);
    deleteElementMeta(videoCreator.currentVideo, oldId);
    await refillSpeakerForCompositionElement({
      elementId: newId,
      editorStore: store,
      datoClientStore: videoCreator.datoClientStore,
      videoCreator,
      controls: template.controls,
      timelineTime: time,
    });
    store.selectElement(newId);
  };
  const handleSaveTemplate = async (
    action: CompositionActions,
    newCompositionName: string,
    overrideValues: ControlOverride[] = updatedValues,
    placementParts: PlacementParts = { position: true, scale: true },
    elementParts: ElementPlacementParts = {},
    sourceOverrides: SourcePropertyOverride[] = [],
    companyLine?: 'add' | 'remove',
  ) => {
    if (!videoCreator.organization || !newCompositionName) return;
    setModalAction(null);
    const updateStart = new Date();
    if (action === 'update') {
      setUpdating(updateStart);
    }

    // Only controls with a defined override value change; the rest keep the
    // template's saved standard verbatim (PLAT-11977).
    let updatedControls: ControlConfiguration[] = applyControlOverrides(
      JSON.parse(JSON.stringify(composition.controls)),
      overrideValues,
    );

    // Label edits rename the source element; saveCompositionTemplate's
    // normalizeTemplateControls then re-derives the label from the new name so
    // it persists (control.label is always derived, never stored directly).
    // Then the element properties set in the modal's More fields.
    let updatedSource = applySourcePropertyOverrides(
      applyLabelOverridesToSource(
        getSourceForSave
          ? getSourceForSave(elementParts, action)
          : composition.source,
        overrideValues,
      ),
      sourceOverrides,
    );

    // The Company Line toggle (PLAT-12130): one rule-based edit of the
    // template's own source and controls, after every other choice.
    if (companyLine) {
      const edit =
        companyLine === 'add'
          ? addCompanyLine(updatedSource, updatedControls, {
              minScale: minFramingScale([
                composition.sixteenByNine,
                composition.nineBySixteen,
                composition.oneByOne,
              ]),
            })
          : removeCompanyLine(updatedSource, updatedControls);
      if (!edit.ok) {
        videoCreator.showToast({
          state: 'error',
          message: `The Company line could not be changed: ${edit.reason}`,
          delay: 5000,
        });
        if (action === 'update') setUpdating(null);
        return;
      }
      updatedSource = edit.source;
      updatedControls = edit.controls;
    }

    // Persist the current on-screen placement (position + size) into the active
    // aspect ratio's config so it survives re-applying the template. The review
    // modal lets the user exclude position and/or scale — excluded parts keep
    // the config values already saved on the template.
    const placement = getCurrentAspectPlacement?.();
    const includePlacement =
      placement && (placementParts.position || placementParts.scale);
    const compositionToSave = {
      ...composition,
      source: updatedSource,
      ...(includePlacement
        ? {
            [placement.key]: {
              ...(composition[placement.key] ?? {}),
              ...(placementParts.position
                ? {
                    positionX: placement.config.positionX,
                    positionY: placement.config.positionY,
                  }
                : {}),
              // Scale is captured per axis; `scale` is dual-written for clients
              // still on the previous bundle. Guarded on scaleX because the
              // capture omits all three when a scale can't be derived (e.g. a
              // fullscreen composition), and the existing config must survive.
              ...(placementParts.scale && placement.config.scaleX !== undefined
                ? {
                    scale: placement.config.scale,
                    scaleX: placement.config.scaleX,
                    scaleY: placement.config.scaleY,
                  }
                : {}),
            },
          }
        : {}),
    };

    const response = await addUpdateFavoriteCompositionTemplate(
      compositionToSave,
      newCompositionName,
      updatedControls,
      categoryId,
      subcategoryId,
      action,
      saveMeta
        ? thumbnail => {
            // Skip if the element was repointed again while this rendered.
            const current = getElementMetaObject(
              videoCreator.currentVideo,
              activeElement.source.id,
            );
            if (current?.templateId !== response?.id) return;
            upsertElementMetaObject(
              videoCreator.currentVideo,
              activeElement.source.id,
              { templateThumbnailUrl: thumbnail.url },
            );
          }
        : undefined,
    );

    if (response && onAfterCreate) {
      onAfterCreate(response);
    }

    if (response && saveMeta) {
      // if saved - associate with new composition template id
      upsertElementMetaObject(
        videoCreator.currentVideo,
        activeElement.source.id,
        {
          ...meta,
          compositionControls: updatedControls,
          templateId: response.id,
          // The thumbnail follows once the new one is rendered.
          templateLabel: response.label,
        },
      );
    }

    // The nametag on screen shows its new speaker lines now, not at its next
    // move (PLAT-12126): only when the speaker section changed, so a save of
    // anything else never overwrites what the creator typed in a bound line.
    const speakerChanged = overrideValues.some(
      o => o.controlBindings !== undefined,
    );
    if (response && saveMeta && editorStore && companyLine) {
      // The nametag on screen is a copy of the template's elements: a new line
      // reaches it only by re-creating it from the saved template (PLAT-12130).
      await replaceElementFromTemplate(response, editorStore);
    } else if (response && saveMeta && editorStore && speakerChanged) {
      void refillSpeakerForCompositionElement({
        elementId: activeElement.source.id,
        editorStore,
        datoClientStore: videoCreator.datoClientStore,
        videoCreator,
        controls: updatedControls,
        timelineTime: Number(activeElement.source.time),
      });
    }

    if (response && action === 'update') {
      onAfterSave?.();
    }

    if (action === 'update') {
      const timeSinceUpdate = new Date().getTime() - updateStart.getTime();
      if (timeSinceUpdate < UPDATE_CLICK_DELAY) {
        setTimeout(() => {
          setUpdating(null);
        }, UPDATE_CLICK_DELAY - timeSinceUpdate);
      } else {
        setUpdating(null);
      }
    }
  };

  // Creator Studio: the whole composition's placement, saved vs on canvas,
  // for the modal's Card block. Read only while the modal is open.
  const currentPlacement = previewOpen
    ? getCurrentAspectPlacement?.()
    : undefined;
  const cardPlacement =
    previewOpen && getCurrentAspectPlacement
      ? {
          before: currentPlacement
            ? composition[currentPlacement.key]
            : undefined,
          after: currentPlacement?.config,
        }
      : undefined;

  const applyRows = applyOpen
    ? buildApplyStandardsRows({
        controls: composition.controls,
        activeElement,
        extraElementData: videoCreator.currentVideo?.extraElementData,
        updatedValues,
        organization: videoCreator.organization,
        story: videoCreator.story,
      })
    : [];

  return (
    <ButtonWrapper>
      <ButtonRow>
        <SaveButton
          // Always available (unless there's no showcase or a save is running):
          // standards can be updated for field labels too, not just changed
          // control values, so we don't gate on pending edits.
          disabled={!videoCreator.organization || !!updating}
          className={!videoCreator.organization ? 'no-showcase' : ''}
          onClick={() =>
            enableApplyStandardsPreview
              ? setPreviewOpen(true)
              : handleSaveTemplate('update', composition.label)
          }
          style={{ padding: '16px 10px' }}
        >
          Set as Graphic Standard
        </SaveButton>
        <InfoAnchor
          data-tooltip-id="standards-info"
          data-tooltip-content={UPDATE_STANDARDS_TIP}
          aria-label="What does Set as Graphic Standard do?"
        >
          <InfoIconSmall />
        </InfoAnchor>
      </ButtonRow>
      {enableApplyStandardsPreview && (
        <ButtonRow>
          <ApplyButton
            disabled={!videoCreator.organization || !editorStore || !!updating}
            className={!videoCreator.organization ? 'no-showcase' : ''}
            onClick={() => setApplyOpen(true)}
          >
            Reset to Graphic Standard
          </ApplyButton>
          <InfoAnchor
            data-tooltip-id="standards-info"
            data-tooltip-content={APPLY_STANDARDS_TIP}
            aria-label="What does Reset to Graphic Standard do?"
          >
            <InfoIconSmall />
          </InfoAnchor>
        </ButtonRow>
      )}
      <SaveNewButton
        disabled={
          !videoCreator.organization ||
          (!updatedValues.length && !positionDirty) ||
          !!updating
        }
        className={!videoCreator.organization ? 'no-showcase' : ''}
        onClick={() => {
          setModalAction(composition.isGlobalPreset ? 'create' : 'save_as');
        }}
      >
        Save New Template WIP
      </SaveNewButton>
      <NoShowcaseMessage className="no-showcase">
        A story needs to be part of a showcase to have default settings
        available.
      </NoShowcaseMessage>
      <Tooltip
        id="standards-info"
        place="top"
        noArrow
        opacity={1}
        // pre-line renders the tips' bullet lines; content is still plain text.
        style={{ ...PARAGRAPH_TOOLTIP_STYLE, whiteSpace: 'pre-line' }}
      />
      {modalAction && (
        <SaveNameModal
          closeModal={() => setModalAction(null)}
          onConfirm={(newCompositionName: string) =>
            handleSaveTemplate(modalAction, newCompositionName)
          }
          onCancel={() => setModalAction(null)}
          confirmText={modalAction === 'update' ? 'Update' : 'Save'}
          cancelText="Cancel"
          headingText={
            modalAction === 'update'
              ? 'Update Template'
              : 'Save Composition Template'
          }
          initialName={composition.label}
        />
      )}
      {previewOpen && (
        <UpdateStandardsModal
          composition={composition}
          updatedValues={updatedValues}
          organization={videoCreator.organization}
          story={videoCreator.story}
          positionDirty={positionDirty}
          isPullQuote={!saveMeta}
          elementLayout={elementLayout}
          cardPlacement={cardPlacement}
          standardSource={standardSource}
          onCancel={() => setPreviewOpen(false)}
          onApply={(
            editedValues,
            placementParts,
            elementParts,
            sourceOverrides,
            companyLine,
          ) => {
            setPreviewOpen(false);
            trackUpdateStandardsApplied({
              surface: saveMeta ? 'creator_studio' : 'pull_quotes',
              controlCount: editedValues.length,
            });
            void handleSaveTemplate(
              'update',
              composition.label,
              editedValues,
              placementParts,
              elementParts,
              sourceOverrides,
              companyLine,
            );
          }}
        />
      )}
      {applyOpen && editorStore && (
        <ApplyStandardsModal
          rows={applyRows}
          onCancel={() => setApplyOpen(false)}
          onConfirm={() => {
            setApplyOpen(false);
            applyTemplateStandardsToElement({
              elementId: activeElement.source.id || '',
              editorStore,
              controls: composition.controls,
              organization: videoCreator.organization,
              story: videoCreator.story,
            });
            trackApplyStandardsApplied({
              surface: saveMeta ? 'creator_studio' : 'pull_quotes',
              changedCount: applyRows.filter(r => r.changed).length,
            });
            videoCreator.showToast({
              state: 'success',
              message: 'Card reset to the graphic standard',
              delay: 3000,
            });
          }}
        />
      )}
    </ButtonWrapper>
  );
};

const SaveButton = styled(SaveButtonKaraokeBase)`
  padding: 16px 10px;
  flex-basis: 100%;
  max-width: 100%;

  /* The Karaoke base flattens opacity to 1, so a disabled button reads as
     enabled. Restore a clear disabled look (dimmed + not-allowed); the
     :disabled selector out-specifies that flat opacity. */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:disabled:hover ~ .no-showcase {
    display: none;
  }

  &:disabled:hover.no-showcase ~ .no-showcase {
    display: flex;
  }
`;

const SaveNewButton = styled(SaveButton)`
  /* De-emphasized: this flow isn't reviewed/finalized yet (WIP). */
  border: 1px dashed var(--border);
  background-color: transparent;
  color: var(--text-muted);

  &:disabled:hover ~ .no-showcase {
    display: none;
  }

  &:disabled:hover.no-showcase ~ .no-showcase {
    display: flex;
  }
`;

const ButtonWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--spacing-sm);
  margin-bottom: -15px;
  margin-top: var(--spacing-xl);
`;

// "Reset to Graphic Standard" enforces the template's saved standards onto the
// current card (template → composition) — a different action from the two green
// template-write buttons, so it reads as a neutral full-width button below them.
const ApplyButton = styled(SaveButton)`
  flex-basis: 100%;
  max-width: 100%;
  border: 1px solid var(--border);
  background-color: transparent;
  color: var(--foreground);

  &:hover:not(:disabled) {
    border-color: var(--text-muted);
  }
`;

// User-facing explanations of the two inverse standards actions. Each spells
// out the direction, what will happen, and what stays untouched (PLAT-11432).
const UPDATE_STANDARDS_TIP = [
  "• Saves this card's current settings as this graphic's standard",
  '• New cards of this graphic will start with these settings',
  '• You review every change and can adjust it before anything is saved',
  '• Only the standard is updated — this card and existing videos stay as they are',
].join('\n');

const APPLY_STANDARDS_TIP = [
  "• Returns this card to the graphic's saved standard settings",
  '• A preview shows exactly what will change before you confirm',
  '• Only this card changes — the saved standard is not modified',
].join('\n');

// Row holding a full-width button with its info icon overlaid at the right edge.
const ButtonRow = styled.div`
  position: relative;
  flex-basis: 100%;
  max-width: 100%;
  display: flex;
`;

// The info affordance is a SIBLING of the button (not a child) so its hover /
// tooltip still fires when the button is disabled, and it sits above the button
// so clicking the icon doesn't trigger the button's action.
const InfoAnchor = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  cursor: help;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--foreground);
  }
`;
